import { useEffect, useState } from "react";

export interface UserLocation {
  latitude: number | null;
  longitude: number | null;
  error?: string;
  permission: PermissionState;
  zoom?: number;
}

const useUserLocation = () => {
  const [location, setLocation] = useState<UserLocation>({
    latitude: null,
    longitude: null,
    permission: "prompt", // default until checked
  });

  // Function to fetch location
  const getCurrentPosition = (permission: PermissionState) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocation((prev) => {
          // Only update if coords or permission changed
          const coordsUnchanged =
            prev.latitude === latitude &&
            prev.longitude === longitude &&
            prev.permission === permission;
          if (coordsUnchanged) {
            // No state update necessary
            return prev;
          }
          return {
            latitude,
            longitude,
            permission,
          };
        });
      },
      (err) => {
        setLocation((prev) => {
          // Avoid updating if error and state is already set
          if (
            prev.latitude === null &&
            prev.longitude === null &&
            prev.permission === permission &&
            prev.error === err.message
          ) {
            return prev;
          }
          return {
            latitude: null,
            longitude: null,
            permission,
            error: err.message,
          };
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocation({
        latitude: null,
        longitude: null,
        permission: "denied",
        error: "Geolocation not supported",
      });
      return;
    }

    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        // Initial permission state
        setLocation((prev) => ({ ...prev, permission: status.state }));

        if (status.state === "granted" || status.state === "prompt") {
          getCurrentPosition(status.state);
        }

        // Listen for permission changes
        status.onchange = () => {
          setLocation((prev) => {
            const newPermission = status.state;

            // Only update if permission changed
            const permissionChanged = prev.permission !== newPermission;
            // Determine if we should fetch location (granted + no coords)
            const shouldFetchLocation =
              newPermission === "granted" &&
              (prev.latitude === null || prev.longitude === null);

            if (permissionChanged || shouldFetchLocation) {
              if (newPermission === "granted") {
                getCurrentPosition("granted");
                // Defer permission update until position is fetched
                return prev;
              } else if (newPermission === "denied") {
                return {
                  ...prev,
                  latitude: null,
                  longitude: null,
                  error: "Geolocation permission denied",
                  permission: "denied",
                };
              } else {
                return { ...prev, permission: newPermission };
              }
            }

            // No changes necessary, return previous state
            return prev;
          });
        };
      })
      .catch((err) => {
        setLocation((prev) => ({
          ...prev,
          permission: "denied",
          error: "Failed to check permission: " + err.message,
        }));
      });
  }, []); // run only once on mount

  return location;
};

export default useUserLocation;
